import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  GET_HIGH_PROBABILITY_SELECTIONS,
  GET_HIGH_PROBABILITY_STRIKE_RATE,
  GET_VALUE_SELECTIONS,
  GET_VALUE_SELECTIONS_STRIKE_RATE,
} from '../../graphql/queries/highProbabilitySelections';
import { GET_MEETINGS_FOR_TRACK_SELECTOR } from '../../graphql/queries/meetingsForTrackSelector';
import { useBrand } from '../brand';
import { STANDOUT_LABEL, STANDOUT_TITLE } from '../tips/standout';
import { useShell, useViewer } from '../shell';
import { useTrack } from '../track';
import { useTourSteps } from '../tour';
import { ChanceRing, Mark, ResultChip, Seg } from '../primitives';
import { TracksDrawer, type DrawerMeeting } from '../chrome';
import { clockTime, money, pct, shortDay } from '../lib/format';
import { datePath, isoDate, parseDatePath } from '../lib/dates';
import { nextRaceByMeeting } from '../home/lib';
import { useLiveRefresh } from '../lib/useLiveRefresh';
import { useUpcoming } from '../home/useUpcoming';
import { PREVIEW_TOUR_STEP, PreviewBar, previewDay } from '../home/PreviewView';
import {
  FALLBACK_THRESHOLD,
  PROBABILITY_THRESHOLD,
  betClass,
  dayScore,
  isLocked,
  isTopRated,
  raceHref,
  recordPct,
  splitPicks,
  valueChance,
  type Record as HpRecord,
  type Selection,
} from './lib';
import './picks.css';

type Period = '7_DAYS' | '1_MONTH';

interface MeetingRow {
  meeting_id: string;
  track: { code: string; name: string; state: string; type: string | null };
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** One product's strike and place rate on the record strip. */
function RecordCol({ name, rec, tone }: { name: string; rec: HpRecord | null | undefined; tone: 'locks' | 'value' }) {
  const p = recordPct(rec);
  return (
    <div className={`ui-hpc ${tone}`}>
      <div className="who">
        <b>
          <i aria-hidden="true" />
          {name}
        </b>
        <span>{rec && p ? `${rec.won}W · ${rec.placed}P · ${rec.resulted}T` : 'No results yet'}</span>
      </div>
      {p && (
        <div className="rates">
          <span>
            <b>{pct(p.won)}</b>Strike
          </span>
          <span>
            <b>{pct(p.top3)}</b>Place
          </span>
        </div>
      )}
    </div>
  );
}

/** The day so far: won, placed, unplaced, then strike and place %. */
function DayCard({ list }: { list: Selection[] }) {
  const s = dayScore(list);
  if (!s) return null;
  return (
    <div className="ui-hpday" data-tour="hp-day">
      <div className="kpi won">
        <div className="kv">
          <i aria-hidden="true" />
          {s.won}
        </div>
        <div className="kl">Won</div>
      </div>
      <div className="kpi placed">
        <div className="kv">
          <i aria-hidden="true" />
          {s.placed}
        </div>
        <div className="kl">Placed</div>
      </div>
      <div className="kpi out">
        <div className="kv">
          <i aria-hidden="true" />
          {s.unplaced}
        </div>
        <div className="kl">Unplaced</div>
      </div>
      <div className="kpi strike">
        <div className="kv">{pct(s.strike, 0)}</div>
        <div className="kl">Strike</div>
      </div>
      <div className="kpi plc">
        <div className="kv">{pct(s.placePct, 0)}</div>
        <div className="kl">Place %</div>
      </div>
    </div>
  );
}

export function HpRow({ sel, kind }: { sel: Selection; kind: 'pick' | 'value' }) {
  const chance = kind === 'value' ? valueChance(sel.assessedOdds) : sel.adjustedAssessedProb;
  return (
    <Link
      className={`ui-hpr ${betClass(sel.betType)}`}
      to={raceHref(sel)}
      title={sel.isStandardClaim ? `Open the race. ${STANDOUT_TITLE}` : 'Open the race'}
      data-tour="hp-row"
      data-standout={!!sel.isStandardClaim || undefined}
    >
      <ChanceRing pct={chance} />
      <span className="hpm">
        <span className="l1">
          <b>
            #{sel.horseNumber} {sel.horseName.replace(/''/g, "'")}
            {sel.isStandardClaim && <span className="ui-vh">{STANDOUT_LABEL}</span>}
          </b>
          <Mark show={!!sel.isConsensusPick} />
        </span>
        <span className="l2">
          {sel.trackName} <i>R{sel.raceNumber}</i>
          {sel.distance ? ` ${sel.distance}m` : ''}
          {sel.raceTime ? ` · ${clockTime(sel.raceTime)}` : ''}
        </span>
        {kind === 'value' && isTopRated(sel) && <span className="l3">Our top rating in the race</span>}
      </span>
      <span className="hpo">
        <ResultChip pos={sel.finishPosition} sp={sel.priceSp} />
        <span>
          {kind === 'value' ? 'Market' : 'Odds'} <b>{money(sel.odds)}</b>
        </span>
        <span>
          Our price <b>{money(sel.assessedOdds)}</b>
        </span>
      </span>
    </Link>
  );
}

/**
 * The high-probability page body (route /high-probability).
 * Reads the day from ?date=YYYYMMDD (default today); prev/next/today are the
 * layout's job: navigate to /high-probability?date=YYYYMMDD (today: no param).
 *
 * `preview` is the signed-out version (/preview/picks/:dateString): past days
 * only, in the preview window, with its own day navigation and sign-up prompt.
 */
export function PicksView({ preview = false }: { preview?: boolean } = {}) {
  const brand = useBrand();
  const viewer = useViewer();
  const { Paywall } = useShell();
  const track = useTrack();
  const nextRace = nextRaceByMeeting(useUpcoming().rows);
  const { search } = useLocation();
  const { dateString } = useParams<{ dateString?: string }>();
  const navigate = useNavigate();
  const pv = preview ? previewDay(dateString) : null;
  const day = pv ? pv.day : parseDatePath(new URLSearchParams(search).get('date')) ?? new Date();
  const skip = !!pv && !pv.valid;
  const iso = isoDate(day);
  const todayIso = isoDate(new Date());
  const isToday = iso === todayIso;
  const locked = isLocked(iso, todayIso, viewer);
  const analysisTypeId = viewer.analysisTypeId || 1;
  const [period, setPeriod] = useState<Period>('7_DAYS');

  // an out-of-range or missing preview date goes to yesterday, as the tips preview does
  useEffect(() => {
    if (skip) navigate(`/preview/picks/${datePath(day)}`, { replace: true });
  }, [skip, day, navigate]);

  const hp = useQuery<{ highProbabilitySelections: Selection[] }>(GET_HIGH_PROBABILITY_SELECTIONS, {
    variables: { date: iso, analysisTypeId, minProbability: FALLBACK_THRESHOLD },
    errorPolicy: 'all',
    skip,
  });
  const val = useQuery<{ valueSelections: Selection[] }>(GET_VALUE_SELECTIONS, {
    variables: { date: iso },
    errorPolicy: 'all',
    skip,
  });
  // race day: today's picks pick up their results as they come in
  const pending = [...(hp.data?.highProbabilitySelections ?? []), ...(val.data?.valueSelections ?? [])].some((x) => x.resultStatus == null);
  useLiveRefresh(isToday && !locked && pending, [hp.refetch, val.refetch]);
  const hpRate = useQuery<{ highProbabilityStrikeRate: HpRecord | null }>(GET_HIGH_PROBABILITY_STRIKE_RATE, {
    variables: { analysisTypeId, minProbability: PROBABILITY_THRESHOLD, timePeriod: period },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const valRate = useQuery<{ valueSelectionsStrikeRate: HpRecord | null }>(GET_VALUE_SELECTIONS_STRIKE_RATE, {
    variables: { timePeriod: period },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const meetings = useQuery<{ meetings: MeetingRow[] }>(GET_MEETINGS_FOR_TRACK_SELECTOR, {
    variables: { date: iso, hasTips: true },
    errorPolicy: 'ignore',
    skip,
  });

  useEffect(() => {
    if (!skip) track('high_probability_view', { brand: brand.id, date: iso, ...(preview && { isPreview: true }) });
  }, [track, brand.id, iso, preview, skip]);

  useEffect(() => {
    if (locked) track('paywall_view', { date: iso });
  }, [track, locked, iso]);

  const tourDay = pv ? 'that day' : 'today';
  useTourSteps([
    ...(pv ? [PREVIEW_TOUR_STEP] : []),
    { target: 'hp-title', title: brand.locksName, body: `Every runner we rate ${PROBABILITY_THRESHOLD}% or better to win ${tourDay}, strongest first.` },
    { target: 'hp-record', title: 'Strike rate', body: 'How often these runners have won (strike) or finished in the top three (place), over the last 7 days or month. W, P and T are wins, places and total runners.' },
    { target: 'hp-day', title: pv ? 'On the day' : 'Today so far', body: 'Won, placed and unplaced once races have run, with the strike and place rate.' },
    {
      target: 'hp-row',
      title: 'A runner',
      body: 'The ring is our chance of winning; the edge colour is the bet (green win, gold place, blue each-way). Tap it to open the race.',
    },
    { target: 'hp-value', title: brand.valueName, body: 'Runners the market has at $8 or more that we rate much shorter. Riskier, but they pay when they land.' },
    { target: 'drawer', title: 'Tracks', body: `Jump to any meeting ${tourDay}.` },
  ]);

  const { picks, shown, isNear } = splitPicks(hp.data?.highProbabilitySelections ?? []);
  const values = val.data?.valueSelections ?? [];
  const dayWord = isToday ? 'today' : `on ${shortDay(iso)}`;
  const ymd = datePath(day);
  const drawerMeetings: DrawerMeeting[] = (meetings.data?.meetings ?? []).map((m) => ({
    key: m.meeting_id,
    name: m.track.name,
    state: m.track.state,
    type: m.track.type,
    href: `/${m.track.state.toLowerCase()}/${m.track.code.toLowerCase()}/${ymd}`,
    next: nextRace.get(m.meeting_id),
  }));

  return (
    <div className="ui-hp">
      {pv && <PreviewBar title={`Past ${brand.locksName}`} base="/preview/picks" day={day} atEarliest={pv.atEarliest} atLatest={pv.atLatest} />}
      <div className="ui-hpt" data-tour="hp-title">
        {!pv && <h1>{brand.locksName}</h1>}
        <p>Every runner we rate {PROBABILITY_THRESHOLD}% or better to win, across all of {isToday ? "today's" : "the day's"} meetings, strongest first.</p>
      </div>

      <section className="ui-hprec" data-tour="hp-record" aria-label="Strike rate">
        <div className="tsh">
          <h3>Strike rate</h3>
          <Seg
            label="Period"
            value={period}
            onChange={(p) => {
              setPeriod(p);
              track('ui_toggle', { control: 'record_period', value: p });
            }}
            options={[
              { value: '7_DAYS', label: '7D' },
              { value: '1_MONTH', label: '1M' },
            ]}
          />
        </div>
        <div className="hpp">
          <RecordCol name={brand.locksName} rec={hpRate.data?.highProbabilityStrikeRate} tone="locks" />
          <RecordCol name={brand.valueName} rec={valRate.data?.valueSelectionsStrikeRate} tone="value" />
        </div>
      </section>

      {locked && Paywall ? (
        <Paywall trackName={brand.locksName} />
      ) : locked ? (
        <div className="ui-hplock">
          <div>
            <div className="t">
              <i aria-hidden="true">🔒</i>{' '}
              {/* the server returns [] to viewers it won't serve, so only show counts it actually sent */}
              {shown.length || values.length
                ? `${shown.length} ${brand.locksName} and ${values.length} ${brand.valueName} runners ${dayWord}`
                : `${brand.locksName} and ${brand.valueName} ${dayWord} are for subscribers`}
            </div>
            <div className="s">Past days are free to view · subscribe for {isToday ? "today's" : "this day's"}</div>
          </div>
          <Link className="ui-hpbtn" to="/pricing" onClick={() => track('ui_click', { target: 'unlock' })}>
            Unlock
          </Link>
        </div>
      ) : viewer.loading && iso >= todayIso ? (
        <p className="ui-hpmsg">Loading selections…</p>
      ) : (
        <>
          <section className="ui-hpsh">
            <h2>{isNear ? `Closest to ${PROBABILITY_THRESHOLD}% ${dayWord}` : `${isToday ? "Today's" : shortDay(iso)} ${brand.locksName}`}</h2>
            <span className="n">{plural(shown.length, 'runner')}</span>
          </section>
          {hp.loading && !hp.data ? (
            <p className="ui-hpmsg">Loading selections…</p>
          ) : hp.error && !hp.data ? (
            <p className="ui-hpmsg">Unable to load selections. Please try again.</p>
          ) : (
            <>
              {isNear && (
                <div className="ui-hpnear">
                  Nothing reached {PROBABILITY_THRESHOLD}% {dayWord}, so these are the nearest ({FALLBACK_THRESHOLD}–{PROBABILITY_THRESHOLD - 1}%). Treat
                  them as a step down from our usual picks.
                </div>
              )}
              <DayCard list={picks} />
              {shown.length ? (
                <div className="ui-hpl">
                  {shown.map((s) => (
                    <HpRow key={`${s.raceId}-${s.horseNumber}`} sel={s} kind="pick" />
                  ))}
                </div>
              ) : (
                <div className="ui-hpnear">Nothing reached our bar {dayWord}, so rather than show weaker tips we've shown none. Try another day.</div>
              )}
            </>
          )}

          <section className="ui-hpsh" data-tour="hp-value">
            <h2>{brand.valueName}</h2>
            <span className="n">{plural(values.length, 'runner')}</span>
          </section>
          <p className="ui-hpsub">Priced $8 or more by the market, but rated much shorter by us.</p>
          {val.loading && !val.data ? (
            <p className="ui-hpmsg">Loading value selections…</p>
          ) : val.error && !val.data ? (
            <p className="ui-hpmsg">Unable to load value selections. Please try again.</p>
          ) : (
            <>
              <DayCard list={values} />
              {values.length ? (
                <div className="ui-hpl">
                  {values.map((s) => (
                    <HpRow key={`value-${s.raceId}-${s.horseNumber}`} sel={s} kind="value" />
                  ))}
                </div>
              ) : (
                <div className="ui-hpnear">No value runners {dayWord}. Try another day.</div>
              )}
            </>
          )}
        </>
      )}

      {drawerMeetings.length > 0 && (
        <TracksDrawer
          title={isToday ? "Today's tracks" : 'Tracks'}
          subtitle={`${plural(drawerMeetings.length, 'meeting')} · ${shortDay(iso)}`}
          meetings={drawerMeetings}
        />
      )}
    </div>
  );
}
